import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Home, Compass, Users, BookMarked, History, MessageSquare, Settings2, ChevronDown, PanelLeftClose, PanelLeftOpen,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCatalogue } from "@/context/CatalogueContext";
import { useSettings } from "@/context/SettingsContext";
import { BrandMark } from "@/components/common/BrandMark";
import { teamColour } from "@/lib/constants";
import { cn } from "@/lib/utils";

const itemClass = (collapsed) => ({ isActive }) =>
  cn(
    "group flex h-10 items-center gap-3 rounded-xl px-3 text-sm transition-quick hover:bg-white/10 hover:text-white",
    isActive ? "bg-white/[0.14] font-medium text-white" : "text-white/85",
    collapsed && "justify-center px-0"
  );

const NavItem = ({ to, label, icon: Icon, end, collapsed, testid }) => (
  <NavLink to={to} end={end} className={itemClass(collapsed)} title={collapsed ? label : undefined} data-testid={testid}>
    <Icon aria-hidden="true" />
    {collapsed ? <span className="sr-only">{label}</span> : <span className="truncate">{label}</span>}
  </NavLink>
);

const liveCount = (team, agents, workflows) =>
  agents.filter((a) => a.team === team.id && !a.parent && a.live).length + workflows.filter((w) => w.team === team.id && w.live).length;

const TeamsGroup = ({ collapsed, onExpand }) => {
  const { teams, agents, workflows, loading, isEmpty } = useCatalogue();
  const [open, setOpen] = useState(true);
  if (collapsed) {
    return (
      <button type="button" onClick={onExpand} title="Teams" className="flex h-10 w-full items-center justify-center rounded-xl text-white/85 transition-quick hover:bg-white/10 hover:text-white" data-testid="nav-teams-collapsed">
        <Users aria-hidden="true" />
        <span className="sr-only">Teams</span>
      </button>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex h-10 w-full items-center gap-3 rounded-xl px-3 text-sm text-white/85 transition-quick hover:bg-white/10 hover:text-white"
        data-testid="nav-teams-toggle"
      >
        <Users aria-hidden="true" />
        <span className="flex-1 text-left">Teams</span>
        <ChevronDown className={cn("!h-4 !w-4 transition-transform duration-150", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open && (
        <div className="mt-1 space-y-0.5 pl-4" data-testid="nav-teams-list">
          {loading && teams.length === 0 && [0, 1, 2, 3, 4].map((i) => <div key={i} className="mx-3 my-2.5 h-5 animate-pulse rounded-md bg-white/10" data-testid="nav-team-skeleton" />)}
          {isEmpty && teams.length === 0 && <p className="px-3 py-2 text-xs text-white/80">Teams appear after the first import</p>}
          {teams.map((t, i) => {
            const n = liveCount(t, agents, workflows);
            return (
              <NavLink key={t.id} to={`/teams/${t.id}`} className={itemClass(false)} data-testid={`nav-team-${t.id}`}>
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: teamColour(t, i) }} aria-hidden="true" />
                <span className="flex-1 truncate">{t.section || t.name}</span>
                {n > 0 && <span className="rounded-full bg-white/15 px-1.5 text-xs tabular-nums text-white" aria-label={`${n} live`} data-testid={`nav-team-live-${t.id}`}>{n}</span>}
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Sidebar = ({ collapsed, onToggle }) => {
  const { user } = useAuth();
  const { settings } = useSettings();
  return (
    <aside
      className={cn("sidebar sticky top-0 hidden h-screen shrink-0 flex-col transition-[width] duration-150 ease-out md:flex", collapsed ? "w-[76px]" : "w-64")}
      aria-label="Main navigation"
      data-testid="sidebar"
    >
      <div className={cn("flex h-16 items-center gap-3 px-4", collapsed && "justify-center px-0")}>
        <BrandMark inverted />
        {!collapsed && <span className="text-sm font-semibold leading-tight text-white" data-testid="sidebar-hub-name">{settings.hub_name}</span>}
      </div>
      <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        <NavItem to="/" end label="Home" icon={Home} collapsed={collapsed} testid="nav-home" />
        <NavItem to="/agents" label="Find an agent" icon={Compass} collapsed={collapsed} testid="nav-find-agent" />
        <TeamsGroup collapsed={collapsed} onExpand={onToggle} />
        <NavItem to="/prompts" label="Prompt Hub" icon={BookMarked} collapsed={collapsed} testid="nav-prompt-hub" />
        <NavItem to="/runs" label="My runs" icon={History} collapsed={collapsed} testid="nav-my-runs" />
        <NavItem to="/feedback" label="Feedback" icon={MessageSquare} collapsed={collapsed} testid="nav-feedback" />
        {user?.role === "owner" && (
          <div className="mt-4 border-t border-white/10 pt-4">
            <NavItem to="/admin" label="Admin" icon={Settings2} collapsed={collapsed} testid="nav-admin" />
          </div>
        )}
      </nav>
      <div className="px-3 pb-4">
        <button
          type="button"
          onClick={onToggle}
          className={cn("flex h-10 w-full items-center gap-3 rounded-xl px-3 text-sm text-white/80 transition-quick hover:bg-white/10 hover:text-white", collapsed && "justify-center px-0")}
          aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <PanelLeftOpen aria-hidden="true" /> : <PanelLeftClose aria-hidden="true" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
};
